import { useEffect, useState } from "react";
import { api, CATEGORIES } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Copy, Newspaper, Sparkles, ExternalLink, CalendarClock } from "lucide-react";
import { Input } from "@/components/ui/input";

const copy = (text, label) => {
  navigator.clipboard.writeText(text || "");
  toast.success(`${label} copied`);
};

const CopyBtn = ({ text, label, tid }) => (
  <button onClick={() => copy(text, label)} data-testid={tid}
    className="h-7 px-2 rounded text-[11px] font-medium bg-zinc-100 hover:bg-zinc-200 text-zinc-600 border border-zinc-200 flex items-center gap-1 transition-colors shrink-0">
    <Copy className="w-3 h-3" /> Copy
  </button>
);

export default function BlogStudio() {
  const [storeUrl, setStoreUrl] = useState(localStorage.getItem("honorifica_store_url") || "https://honorifica.com");
  const [topics, setTopics] = useState({ upcoming: [], evergreen: [] });
  const [customTopic, setCustomTopic] = useState("");
  const [customCat, setCustomCat] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [activeKey, setActiveKey] = useState("");

  useEffect(() => {
    api.get("/marketing/blog/topics").then(({ data }) => setTopics(data)).catch(() => {});
  }, []);
  useEffect(() => { localStorage.setItem("honorifica_store_url", storeUrl); }, [storeUrl]);

  const generate = async (t) => {
    setBusy(true); setActiveKey(t.key || "custom"); setResult(null);
    try {
      const { data } = await api.post("/marketing/blog/brief", {
        topic: t.title, occasion: t.occasion || "", category: t.category || "",
        theme: t.theme || "", store_url: storeUrl, image_base: window.location.origin, count: 5,
      });
      setResult(data);
      toast.success("Blog brief ready");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not generate brief");
    } finally { setBusy(false); }
  };

  const genCustom = () => {
    if (!customTopic.trim()) { toast.info("Type a topic first"); return; }
    generate({ key: "custom", title: customTopic.trim(), occasion: customTopic.trim(), category: customCat, theme: "" });
  };

  const b = result?.brief || {};

  return (
    <div className="max-w-6xl mx-auto px-6 py-8" data-testid="blog-studio">
      <p className="text-[13px] font-semibold tracking-widest uppercase text-[#C05A45]">Feed Shopify's AI blog</p>
      <h1 className="text-4xl font-serif text-zinc-900 mt-1" style={{ fontFamily: "Georgia, serif" }}>Blog Studio</h1>
      <p className="text-zinc-600 mt-2 max-w-2xl text-sm">
        Honorifica picks the topics and the products to feature; paste the brief into Shopify's AI blog
        writer, then drop in the product links & images below. Ready-made products only.
      </p>

      <div className="mt-6 flex items-end gap-3">
        <label className="text-xs text-zinc-500">Store URL
          <Input value={storeUrl} onChange={(e) => setStoreUrl(e.target.value)} data-testid="blog-store-url"
            className="mt-1 h-9 w-72" placeholder="https://honorifica.com" />
        </label>
      </div>

      <div className="grid lg:grid-cols-[360px_minmax(0,1fr)] gap-6 mt-6">
        {/* Topic picker */}
        <div className="space-y-5 min-w-0">
          <div className="border border-zinc-200 rounded-xl p-4">
            <div className="flex items-center gap-2 text-sm font-semibold text-zinc-800">
              <CalendarClock className="w-4 h-4 text-[#C05A45]" /> Upcoming occasions
            </div>
            <div className="mt-3 space-y-2" data-testid="upcoming-topics">
              {topics.upcoming.map((t) => (
                <button key={t.key} onClick={() => generate(t)} disabled={busy}
                  data-testid={`topic-${t.key}`}
                  className={`w-full text-left px-3 py-2 rounded-lg border transition-colors disabled:opacity-50 ${activeKey === t.key ? "border-[#C05A45] bg-[#faf1ee]" : "border-zinc-200 hover:bg-zinc-50"}`}>
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-sm text-zinc-800">{t.emoji} {t.occasion}</span>
                    <span className="text-[11px] text-zinc-400 shrink-0">{t.days_until}d · {t.category}</span>
                  </div>
                </button>
              ))}
            </div>
          </div>

          <div className="border border-zinc-200 rounded-xl p-4">
            <div className="text-sm font-semibold text-zinc-800">Evergreen guides</div>
            <div className="mt-3 space-y-2">
              {topics.evergreen.map((t) => (
                <button key={t.key} onClick={() => generate(t)} disabled={busy}
                  data-testid={`topic-${t.key}`}
                  className={`w-full text-left px-3 py-2 rounded-lg border transition-colors disabled:opacity-50 ${activeKey === t.key ? "border-[#C05A45] bg-[#faf1ee]" : "border-zinc-200 hover:bg-zinc-50"}`}>
                  <span className="text-sm text-zinc-800">{t.title}</span>
                  <span className="block text-[11px] text-zinc-400">{t.category}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="border border-zinc-200 rounded-xl p-4">
            <div className="text-sm font-semibold text-zinc-800">Your own topic</div>
            <Input value={customTopic} onChange={(e) => setCustomTopic(e.target.value)} data-testid="custom-topic-input"
              placeholder="e.g. Gifts for a Coworker's Retirement" className="mt-3 h-9" />
            <select value={customCat} onChange={(e) => setCustomCat(e.target.value)} data-testid="custom-topic-cat"
              className="mt-2 h-9 w-full bg-white border border-zinc-300 rounded-md px-2 text-sm outline-none">
              <option value="">Any category</option>
              {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <button onClick={genCustom} disabled={busy} data-testid="generate-custom-btn"
              className="mt-3 w-full h-9 rounded-md bg-[#C05A45] hover:bg-[#a94b39] text-white text-sm font-semibold flex items-center justify-center gap-1.5 transition-colors disabled:opacity-50">
              <Sparkles className="w-4 h-4" /> Generate brief
            </button>
          </div>
        </div>

        {/* Brief output */}
        <div className="border border-zinc-200 rounded-xl p-5 min-h-[400px] min-w-0 overflow-hidden" data-testid="brief-panel">
          {busy && (
            <div className="h-full flex flex-col items-center justify-center text-zinc-400 py-20">
              <Loader2 className="w-6 h-6 animate-spin" />
              <p className="text-sm mt-3">Writing your brief…</p>
            </div>
          )}
          {!busy && !result && (
            <div className="h-full flex flex-col items-center justify-center text-zinc-400 py-20 text-center">
              <Newspaper className="w-8 h-8" />
              <p className="text-sm mt-3 max-w-xs">Pick an occasion or type your own topic to generate a paste-ready blog brief.</p>
            </div>
          )}
          {!busy && result && (
            <div className="space-y-5" data-testid="brief-result">
              <div>
                <div className="flex items-center justify-between">
                  <h2 className="text-lg font-semibold text-zinc-900">{result.topic}</h2>
                </div>
                <p className="text-sm text-zinc-500 mt-0.5">{b.angle}</p>
                <p className="text-[11px] text-zinc-400 mt-1">Each block below maps to a field in Shopify's "Add blog post" screen — copy it into the matching field.</p>
              </div>

              <section>
                <div className="text-xs font-semibold uppercase tracking-wide text-zinc-400 mb-2">→ Title (pick one)</div>
                <div className="space-y-1.5">
                  {(b.titles || []).map((t, i) => (
                    <div key={i} className="flex items-center justify-between gap-2 bg-zinc-50 rounded-lg px-3 py-2">
                      <span className="text-sm text-zinc-800">{t}</span>
                      <CopyBtn text={t} label="Title" tid={`copy-title-${i}`} />
                    </div>
                  ))}
                </div>
              </section>

              <section className="bg-[#faf1ee] border border-[#e8cfc7] rounded-lg p-4">
                <div className="text-xs font-semibold uppercase tracking-wide text-[#a94b39] mb-1 flex items-center justify-between">
                  → Content (paste into the ✨ AI generator) <CopyBtn text={b.shopify_ai_prompt} label="Content prompt" tid="copy-shopify-prompt" />
                </div>
                <p className="text-sm text-zinc-700 whitespace-pre-wrap">{b.shopify_ai_prompt}</p>
                <p className="text-[11px] text-zinc-500 mt-2">In Shopify's Content box, click the ✨ icon → paste this → generate. Then add the product cards below.</p>
              </section>

              <div className="grid sm:grid-cols-2 gap-4">
                <section>
                  <div className="text-xs font-semibold uppercase tracking-wide text-zinc-400 mb-1 flex items-center justify-between">
                    → Excerpt <CopyBtn text={b.excerpt} label="Excerpt" tid="copy-excerpt" />
                  </div>
                  <div className="text-sm text-zinc-700 bg-zinc-50 rounded-lg px-3 py-2">{b.excerpt}</div>
                </section>
                <section>
                  <div className="text-xs font-semibold uppercase tracking-wide text-zinc-400 mb-1 flex items-center justify-between">
                    → Tags <CopyBtn text={(b.tags || []).join(", ")} label="Tags" tid="copy-tags" />
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {(b.tags || []).map((k, i) => (
                      <span key={i} className="text-xs bg-zinc-100 text-zinc-600 rounded-full px-2.5 py-1">{k}</span>
                    ))}
                  </div>
                </section>
              </div>

              <div className="border-t border-zinc-100 pt-4">
                <div className="text-xs font-semibold uppercase tracking-wide text-zinc-400 mb-2">→ Search engine listing</div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <section>
                    <div className="text-[11px] font-medium text-zinc-500 mb-1 flex items-center justify-between">
                      SEO title <CopyBtn text={b.seo_title} label="SEO title" tid="copy-seo-title" />
                    </div>
                    <div className="text-sm text-zinc-800 bg-zinc-50 rounded-lg px-3 py-2">{b.seo_title}</div>
                  </section>
                  <section>
                    <div className="text-[11px] font-medium text-zinc-500 mb-1 flex items-center justify-between">
                      SEO description <CopyBtn text={b.meta_description} label="Meta" tid="copy-meta" />
                    </div>
                    <div className="text-sm text-zinc-700 bg-zinc-50 rounded-lg px-3 py-2">{b.meta_description}</div>
                  </section>
                </div>
              </div>

              <section>
                <div className="text-xs font-semibold uppercase tracking-wide text-zinc-400 mb-1 flex items-center justify-between">
                  Keywords (reference) <CopyBtn text={[b.primary_keyword, ...(b.secondary_keywords || [])].filter(Boolean).join(", ")} label="Keywords" tid="copy-secondary-kw" />
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {b.primary_keyword && <span className="text-xs bg-[#faf1ee] text-[#a94b39] font-medium rounded-full px-2.5 py-1">{b.primary_keyword}</span>}
                  {(b.secondary_keywords || []).map((k, i) => (
                    <span key={i} className="text-xs bg-zinc-100 text-zinc-600 rounded-full px-2.5 py-1">{k}</span>
                  ))}
                </div>
              </section>

              <section>
                <div className="text-xs font-semibold uppercase tracking-wide text-zinc-400 mb-1 flex items-center justify-between">
                  Outline (reference for editing) <CopyBtn text={(b.outline || []).map((s, i) => `${i + 1}. ${s}`).join("\n")} label="Outline" tid="copy-outline" />
                </div>
                <ol className="text-sm text-zinc-700 list-decimal ml-5 space-y-1">
                  {(b.outline || []).map((s, i) => <li key={i}>{s}</li>)}
                </ol>
              </section>

              <section>
                <div className="text-xs font-semibold uppercase tracking-wide text-zinc-400 mb-2">
                  Featured products ({(result.featured || []).length})
                </div>
                <div className="space-y-2" data-testid="featured-products">
                  {(result.featured || []).map((f, i) => (
                    <div key={i} className="flex items-center gap-3 border border-zinc-200 rounded-lg p-2">
                      {f.image
                        ? <img src={f.image} alt={f.title} className="w-14 h-14 rounded object-cover shrink-0" />
                        : <div className="w-14 h-14 rounded bg-zinc-100 shrink-0" />}
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium text-zinc-800 truncate">{f.title}</div>
                        <p className="text-xs text-zinc-500 line-clamp-2">{f.pitch}</p>
                        <a href={f.url} target="_blank" rel="noreferrer" className="text-[11px] text-[#C05A45] hover:underline flex items-center gap-1 mt-0.5 min-w-0">
                          <ExternalLink className="w-3 h-3 shrink-0" /> <span className="truncate">{f.url}</span>
                        </a>
                      </div>
                      <div className="flex flex-col gap-1 shrink-0">
                        <CopyBtn text={f.url} label="Link" tid={`copy-prod-url-${i}`} />
                        {f.image && <CopyBtn text={f.image} label="Image URL" tid={`copy-prod-img-${i}`} />}
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
